import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, UserPlus, Sparkles, Check, X } from "lucide-react";

export default function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Password validation
  const passwordChecks = {
    length: password.length >= 6,
    match: password === confirmPassword && confirmPassword.length > 0,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!passwordChecks.length) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!passwordChecks.match) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);
    
    const result = await register(email, password);
    
    if (result.success) {
      toast.success("Account created! Now choose your player name.");
      navigate("/setup-name");
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen game-gradient-bg pattern-dots flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-playful border-2 border-border">
        <CardHeader className="text-center space-y-2">
          {/* Logo */}
          <div className="flex justify-center mb-2">
            <div className="relative">
              <span className="text-5xl">🍎</span>
              <Sparkles className="absolute -top-1 -right-2 w-5 h-5 text-warning animate-sparkle" />
            </div>
          </div>
          
          <CardTitle className="font-display text-3xl font-bold">
            Join{" "}
            <span className="text-fruit-banana">F</span>
            <span className="text-fruit-apple">r</span>
            <span className="text-fruit-pear">u</span>
            <span className="text-fruit-pineapple">i</span>
            <span className="text-fruit-orange">t</span>
            {" "}
            <span className="text-primary">Crush</span>
          </CardTitle>
          
          <CardDescription className="font-body text-muted-foreground">
            Create your account and start crushing!
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="font-body font-medium">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 font-body"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="font-body font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 font-body"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-body font-medium">
                Confirm Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 font-body"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Requirements */}
            <div className="space-y-1 p-3 bg-muted rounded-lg">
              <p className="font-body text-sm font-medium text-muted-foreground mb-2">
                Password requirements:
              </p>
              <div className="flex items-center gap-2">
                {passwordChecks.length ? (
                  <Check className="w-4 h-4 text-success" />
                ) : (
                  <X className="w-4 h-4 text-muted-foreground" />
                )}
                <span className={`text-sm font-body ${passwordChecks.length ? 'text-success' : 'text-muted-foreground'}`}>
                  At least 6 characters
                </span>
              </div>
              <div className="flex items-center gap-2">
                {passwordChecks.match ? (
                  <Check className="w-4 h-4 text-success" />
                ) : (
                  <X className="w-4 h-4 text-muted-foreground" />
                )}
                <span className={`text-sm font-body ${passwordChecks.match ? 'text-success' : 'text-muted-foreground'}`}>
                  Passwords match
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              size="lg"
              className="w-full font-display font-semibold text-lg bg-success hover:bg-success/90 text-success-foreground shadow-button"
              disabled={isLoading || !passwordChecks.length || !passwordChecks.match}
            >
              {isLoading ? (
                <span className="animate-pulse">Creating account...</span>
              ) : (
                <>
                  <UserPlus className="w-5 h-5 mr-2" />
                  Create Account
                </>
              )}
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="font-body text-muted-foreground">
              Already have an account?{" "}
              <Link 
                to="/login" 
                className="font-semibold text-primary hover:underline"
              >
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
